<script setup lang="ts">
import { capitalize } from 'lodash'
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useCan } from '@/modules/auth/composables/use-can'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { rails_url } from '@/modules/shared/utils/rails'
import VTabList from '@/modules/shared/components/v-tab-list.vue'
import { useOutstandingStore } from '@/modules/dashboard/stores/outstanding-store'
import { useOtherEntityStore } from '../stores/other-entity-store'

const { can } = useCan()
const { t } = useI18n()
const authStore = useAuthStore()
const route = useRoute()
const outstandingStore = useOutstandingStore()
const otherEntityStore = useOtherEntityStore()

const id = route.params.other_entity_id
const entityURL = computed(() => `${rails_url()}/funding-entities/${id}`)
const currentOtherEntity = computed(() => otherEntityStore.items.get(`funding-entity:${id}`))

const isCurrent = (name: string) => route.name === name

const outstanding = computed(() => outstandingStore.outstanding)

onMounted(async () => {
  await Promise.all([
    await otherEntityStore.fetchOtherEntity(`${id}`),
    outstandingStore.fetchOutstandings(`funding-entity:${id}`),
  ])
})
</script>

<template>
  <div class="mb-12 px-4 sm:px-0">
    <VTabList
      :tabs="[
        {
          current: isCurrent('investing.other-entity.overview'),
          name: capitalize(t('shared.overview')),
          to: {
            name: 'investing.other-entity.overview',
          },
        },
        {
          current: isCurrent('investing.other-entity.portfolio'),
          name: capitalize(t('shared.portfolio')),
          to: {
            name: 'investing.other-entity.portfolio',
          },
        },
        {
          current: isCurrent('investing.other-entity.funds'),
          name: 'Funds',
          to: {
            name: 'investing.other-entity.funds',
          },
        },
        {
          current: isCurrent('investing.other-entity.spvs'),
          name: 'SPVs',
          to: {
            name: 'investing.other-entity.spvs',
          },
        },
        {
          current: isCurrent('investing.other-entity.associated-entities'),
          name: 'Associated Entities',
          to: {
            name: 'investing.other-entity.associated-entities',
          },
          count: outstanding.incomplete_associated_entities_count,
        },
        {
          current: isCurrent('investing.other-entity.invoices'),
          name: capitalize(t('shared.invoice', 0)),
          to: {
            name: 'investing.other-entity.invoices',
          },
          count: outstanding.invoices_count,
        },
        {
          current: isCurrent('investing.other-entity.investors'),
          name: capitalize(t('shared.investor', 0)),
          to: {
            name: 'investing.other-entity.investors',
          },
        },
        {
          current: isCurrent('investing.other-entity.documents'),
          name: t('shared.eSignature', 0),
          to: {
            name: 'investing.other-entity.documents',
          },
          count: outstanding.documents_count,
        },
        {
          current: isCurrent('filing.other-entity.files-system'),
          name: 'Files',
          to: {
            name: 'filing.other-entity.files-system',
            params: {
              entity_id: id,
            },
          },
        },
        {
          current: isCurrent('investing.other-entity.notes'),
          name: capitalize(t('shared.note', 0)),
          to: {
            name: 'investing.other-entity.notes',
          },
          visible: can('admin', `${id}`),
        },
        {
          current: false,
          name: 'Files (legacy)',
          to: `${entityURL}/files?legacy=true`,
          visible: authStore.is_site_admin,
        },
      ]"
    />
  </div>
</template>
